/**
 * Wire shapes for the OR-6089 chat-card + refetch events as they cross
 * the Socket.IO boundary between the message-gateway and the FE.
 *
 * Two events live here:
 *   - `RESOURCE_CARD` adds or refreshes a clickable chat card pointing
 *     at a project resource (cohort / analytical variable / concept group).
 *   - `RESOURCE_UPDATED` signals server state changed for an existing
 *     resource so the FE can `queryClient.invalidateQueries(...)` for
 *     the kind-specific keys.
 *
 * Both payloads are camelCase on the wire (BE-side constructors stamp
 * the camelCase fields directly into the AgUiEvent payload — see
 * `protocol_schema.event_schemas.AgUiEvent.resource_card` /
 * `.resource_updated`). Validate at the trust boundary in
 * `useEpiChatStream`; drop malformed payloads with a logged warning
 * rather than throwing — mirrors the WireScopeSchema pattern.
 */
import { z } from 'zod';

import type { EpiResourceCardStatus, EpiResourceKind } from '../types/epi-chat-types';

const RESOURCE_KINDS = ['cohort', 'analytical_variable', 'concept_group', 'analysis'] as const;
const RESOURCE_CARD_STATUSES = ['planning', 'drafting', 'updating', 'ready', 'error'] as const;

// Compile-time guard so the wire-schema enums and the FE type union stay
// aligned without a generated-file dance. If the union diverges, the
// `satisfies` cast fails to type-check at build time.
type _KindGuard = (typeof RESOURCE_KINDS)[number] extends EpiResourceKind ? true : never;
type _StatusGuard = (typeof RESOURCE_CARD_STATUSES)[number] extends EpiResourceCardStatus
  ? true
  : never;
const _guards: [_KindGuard, _StatusGuard] = [true, true];
void _guards;

export const ResourceCardPayloadSchema = z
  .object({
    resourceKind: z.enum(RESOURCE_KINDS),
    projectId: z.string().min(1),
    resourceId: z.string().min(1).optional(),
    clientId: z.string().min(1).optional(),
    name: z.string(),
    status: z.enum(RESOURCE_CARD_STATUSES),
    route: z.string().min(1),
    hintArgs: z.record(z.string(), z.unknown()).optional(),
  })
  // At least one of resourceId / clientId — both are allowed when the
  // emit is a ratchet (analytical variable pipeline rotating its `clientId` slot into a
  // real `av.<slug>`). The reducer matches by clientId first, then
  // resourceId, and folds in whichever id is newly populated.
  .refine((value) => value.resourceId !== undefined || value.clientId !== undefined, {
    message: 'RESOURCE_CARD requires at least one of resourceId / clientId',
  });

export type ResourceCardPayload = z.infer<typeof ResourceCardPayloadSchema>;

/**
 * OR-6125 `UPLOADED_FILE` payload — the uploaded-file chip. Distinct from the
 * resource-card payload (a file is not a project resource): just the display /
 * download name and the upload id that keys the stored bytes for re-download
 * (upload-scoped, so the chip works even for an unrecognized file with no preview).
 */
export const UploadedFilePayloadSchema = z.object({
  fileName: z.string().min(1),
  uploadId: z.string().min(1),
  projectId: z.string().min(1),
});

export type UploadedFilePayload = z.infer<typeof UploadedFilePayloadSchema>;

/**
 * OR-6089 / OR-6078: optional patch the FE applies to its local store
 * BEFORE the server-confirmed refetch lands so the user sees the result
 * immediately. Three kinds are wired today:
 *   - ``av_attached`` — ``add_analytical_variable_to_cohort`` binds an AV
 *     into a stub criterion (swap by ``stubIndex``).
 *   - ``av_replaced`` — ``replace_analytical_variable_in_cohort`` (OR-6073)
 *     swaps the bound AV on an existing criterion (swap by ``criterionIndex``).
 *   - ``criterion_removed`` — ``remove_criterion_from_cohort`` (OR-6074)
 *     drops one or more criteria (by their positions in ``removedIndices``).
 * The chat reducer dispatches by ``kind``; unknown kinds are dropped quietly
 * (forward-compat — a newer backend can emit a kind an older FE ignores).
 *
 * Shapes mirror the backend serialization in ``tools/cohort_composition.py``:
 * ``newCriterion`` is the bound criterion object exactly as it lands in the
 * cohort version's ``jsonValue.criteria`` array. Reducer-side validation
 * (against the Zustand criterion union) catches any drift between backend and
 * FE schemas.
 */
// Analytical variable display fields so the FE can synthesize a library-list
// entry in the TanStack cache (``useCriteriaSection`` resolves
// ``sourceLibraryAvId`` via that cache). Without this, the criteria card stays
// in the unresolved stub state until the library-list refetch round-trip
// completes (1-2s+ when Snowflake is slow). Shared by ``av_attached`` and
// ``av_replaced`` — both surface a freshly-bound AV.
const OptimisticAvDisplaySchema = z.object({
  analyticalVariableId: z.string().min(1),
  name: z.string(),
  description: z.string(),
  dataElement: z.string(),
  kind: z.enum(['av_ir', 'av_denovo']),
});

const OptimisticAvAttachedPatchSchema = z.object({
  kind: z.literal('av_attached'),
  // Zero-based position of the stub criterion to swap the bound AV into.
  stubIndex: z.number().int().min(0),
  newCriterion: z.record(z.string(), z.unknown()),
  avDisplay: OptimisticAvDisplaySchema.optional(),
});

const OptimisticAvReplacedPatchSchema = z.object({
  kind: z.literal('av_replaced'),
  // Zero-based position (in the full ``criteria`` array) of the criterion
  // whose bound analytical variable was swapped.
  criterionIndex: z.number().int().min(0),
  newCriterion: z.record(z.string(), z.unknown()),
  avDisplay: OptimisticAvDisplaySchema.optional(),
});

const OptimisticCriterionRemovedPatchSchema = z.object({
  kind: z.literal('criterion_removed'),
  // Zero-based positions (in the full ``criteria`` array) of the removed
  // criteria. Multiple when a description match removed several at once.
  removedIndices: z.array(z.number().int().min(0)).min(1),
});

export const OptimisticPatchSchema = z.discriminatedUnion('kind', [
  OptimisticAvAttachedPatchSchema,
  OptimisticAvReplacedPatchSchema,
  OptimisticCriterionRemovedPatchSchema,
]);
export type OptimisticPatch = z.infer<typeof OptimisticPatchSchema>;

export const ResourceUpdatedPayloadSchema = z.object({
  resourceKind: z.enum(RESOURCE_KINDS),
  resourceId: z.string().min(1),
  projectId: z.string().min(1),
  // Informational only — no reducer branches on it, so an out-of-enum value
  // must NOT fail the parse and drop the whole event. The draft-native AV
  // save once emitted a bespoke "saved": the enum parse rejected the event,
  // no cache invalidated, and the chat chip / detail header froze on Draft
  // until a manual refresh. Free string keeps the invalidation delivery
  // robust to backend operation-vocabulary drift.
  operation: z.string().min(1),
  parentIds: z.record(z.string(), z.string()).optional(),
  optimisticPatch: OptimisticPatchSchema.optional(),
  // OR-6158: status transition the FE reflects without a refetch. Only
  // `process_cohort` sets it (`'PROCESSING'`). Free string for forward-compat.
  newStatus: z.string().min(1).optional(),
  // AV save only — dependents are already stale server-side; refetch signal.
  // Broad (no usage lookup), so it covers every cached kind, not just the
  // AV's actual dependents.
  refreshAssetStaleness: z.boolean().optional(),
});

export type ResourceUpdatedPayload = z.infer<typeof ResourceUpdatedPayloadSchema>;
